import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonFab,
  IonFabButton,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonLoading,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSearchbar,
  IonItem,
  IonToggle,
} from "@ionic/react";
import { add, chevronDownCircleOutline } from "ionicons/icons";
import { useRecoilValue } from "recoil";
import { userProfileState } from "../services/state.service";
import { useEffect, useState } from "react";
import {
  getTickets,
  getNearestTickets,
  parseRegionIds,
} from "../services/ticket.service";
import { GeolocationService } from "../services/geolocation.service";
import { useLocation } from "react-router";
import useNavigation from "../hooks/useNavigation";
import Tickets from "../components/Tickets";

const PER_PAGE = 50;
const OLDEST_FIRST = ['created_date ASC'];

const TicketList: React.FC = () => {
  const navigateTo = useNavigation();
  const location = useLocation();
  const userProfile: any = useRecoilValue(userProfileState);
  const updated: any = (location.state as any)?.updated ?? false;
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [activeTab, setActiveTab] = useState<'assigned' | 'region'>('assigned');
  const [skip, setSkip] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [originalTickets, setOriginalTickets] = useState<any[]>([]);
  const [showNearestTickets, setShowNearestTickets] = useState(false);
  const [nearMeCache, setNearMeCache] = useState<any[] | null>(null);

  const fetchNearestTicketsData = async () => {
    setLoading(true);
    try {
      const geolocationService = new GeolocationService();
      const pos = await geolocationService.getCurrentPosition();
      const { latitude, longitude } = pos.coords;
      const where: any = { status: 'open' };
      if (activeTab === 'assigned' && userProfile?.farmUserId) {
        where.assigneeId = userProfile.farmUserId;
      }
      const nearest = await getNearestTickets(latitude, longitude, where, 50);
      setTickets(nearest);
    } catch (err) {
      console.error('Geolocation error', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const sortTicketsByLocation = async () => {
    if (!showNearestTickets) {
      setNearMeCache(tickets);
      setShowNearestTickets(true);
      await fetchNearestTicketsData();
    } else {
      if (nearMeCache) setTickets(nearMeCache);
      setNearMeCache(null);
      setShowNearestTickets(false);
    }
  };

  const fetchRegionTickets = async (skipOffset: number): Promise<{ tickets: any[]; done: boolean }> => {
    const regionIds: string[] = userProfile?.regionIds ?? [];
    if (!regionIds.length) return { tickets: [], done: true };
    const regionKeys = parseRegionIds(regionIds);
    if (!regionKeys.length) return { tickets: [], done: true };
    const res = await getTickets({ regionId: { inq: regionKeys }, status: 'open' }, PER_PAGE, skipOffset, OLDEST_FIRST);
    return { tickets: res.tickets, done: res.tickets.length < PER_PAGE };
  };

  const fetchData = async (event?: CustomEvent, isInitial: boolean = true, skipOffset: number = 0) => {
    if (isInitial) {
      setLoading(true);
      setHasError(false);
      setSkip(0);
      setDisableInfiniteScroll(false);
    }

    try {
      let result: { tickets: any[]; done: boolean };

      if (activeTab === 'assigned') {
        if (!userProfile?.farmUserId) throw new Error('No user');
        const res = await getTickets(
          { assigneeId: userProfile.farmUserId, status: 'open' },
          PER_PAGE,
          isInitial ? 0 : skipOffset,
          OLDEST_FIRST,
        );
        result = { tickets: res.tickets, done: res.tickets.length < PER_PAGE };
      } else {
        result = await fetchRegionTickets(isInitial ? 0 : skipOffset);
      }

      setHasError(false);
      if (isInitial) {
        setTickets(result.tickets);
        setOriginalTickets(result.tickets);
      } else {
        setTickets(prev => [...prev, ...result.tickets]);
        setOriginalTickets(prev => [...prev, ...result.tickets]);
      }
      setDisableInfiniteScroll(result.done);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setHasError(true);
    } finally {
      setLoading(false);
      (event as any)?.detail?.complete?.();
    }
  };

  const handleRefresh = (e: any) => {
    setSearchQuery('');
    setIsSearching(false);
    setShowNearestTickets(false);
    setNearMeCache(null);
    fetchData(undefined, true);
    e.detail.complete();
  };

  const handleTabChange = (event: CustomEvent) => {
    const value = event.detail.value as 'assigned' | 'region';
    setActiveTab(value);
    setSkip(0);
    setDisableInfiniteScroll(false);
    setSearchQuery('');
    setIsSearching(false);
    setShowNearestTickets(false);
    setNearMeCache(null);
  };

  const handleSearch = async (query: string) => {
    if (showNearestTickets) return;
    setSearchQuery(query);

    if (!query.trim()) {
      setTickets(originalTickets);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setLoading(true);

    try {
      const q = query.trim();
      const searchOr = [
        { ticketNumber: { regexp: `/${q}/i` } },
        { farmerName: { regexp: `/${q}/i` } },
        { farmerMobile: { regexp: `/${q}/i` } },
        { farmUserId: { regexp: `/${q}/i` } },
        { assigneeId: { regexp: `/${q}/i` } },
        { plotId: { regexp: `/${q}/i` } },
      ];

      let where: any;
      if (activeTab === 'region') {
        const regionKeys = parseRegionIds(userProfile?.regionIds ?? []);
        where = { and: [{ regionId: { inq: regionKeys }, status: 'open' }, { or: searchOr }] };
      } else {
        where = { and: [{ assigneeId: userProfile?.farmUserId, status: 'open' }, { or: searchOr }] };
      }

      const res = await getTickets(where, PER_PAGE, 0, OLDEST_FIRST);
      setTickets(res.tickets);
    } catch (error) {
      console.error("Error searching tickets:", error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchNext = async (event: CustomEvent<void>) => {
    if (showNearestTickets) {
      (event.target as HTMLIonInfiniteScrollElement).complete();
      return;
    }
    const nextSkip = skip + PER_PAGE;
    setSkip(nextSkip);
    await fetchData(undefined, false, nextSkip);
    (event.target as HTMLIonInfiniteScrollElement).complete();
  };

  useEffect(() => {
    if (updated) fetchData(undefined, true);
  }, [updated]);

  useEffect(() => {
    setSkip(0);
    setDisableInfiniteScroll(false);
    fetchData(undefined, true);
  }, [activeTab]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Tickets</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Tab Segment */}
        <IonSegment value={activeTab} onIonChange={handleTabChange}>
          <IonSegmentButton value="assigned">
            <IonLabel>Assigned to Me</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="region">
            <IonLabel>By Region</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {/* Near Me Toggle */}
        <IonItem lines="none">
          <IonLabel>Show nearest tickets</IonLabel>
          <IonToggle
            checked={showNearestTickets}
            onClick={sortTicketsByLocation}
            slot="end"
          />
        </IonItem>

        {/* Search Box */}
        <IonItem>
          <IonSearchbar
            value={searchQuery}
            placeholder="Search by farmer name, mobile, plot ID, assignee ID, TKT-#..."
            onIonChange={(e) => handleSearch(e.detail.value || '')}
            onIonClear={() => handleSearch('')}
            showClearButton="focus"
            debounce={1500}
            disabled={showNearestTickets}
          />
        </IonItem>

        <IonLoading isOpen={loading} message="Loading tickets..." spinner="circles" />

        {/* Pull to refresh */}
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          />
        </IonRefresher>

        {/* Error Handling */}
        {hasError ? (
          <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
            <p>
              {activeTab === 'region' && !userProfile?.regionIds?.length
                ? "No regions assigned to your profile. Please contact your administrator."
                : "Error fetching tickets. Please refresh the page."
              }
            </p>
          </div>
        ) : (
          <>
            <Tickets tickets={tickets} />

            <IonInfiniteScroll
              disabled={disableInfiniteScroll || isSearching || showNearestTickets}
              onIonInfinite={fetchNext}
              threshold="100px"
            >
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Loading more tickets..."
              />
            </IonInfiniteScroll>
          </>
        )}

        {/* Floating Action Button */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => navigateTo("/tabs/ticket", { newTicket: true })}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

export default TicketList;
